<template>
  <a-modal :visible="visible" :title="paymentTitle" :footer="null" :maskClosable="false" @cancel="handleCancel">
    <div class="payment-modal-content">
      <div class="order-info">
        <div class="order-item">
          <span class="order-label">套餐名称：</span>
          <a-tooltip placement="topLeft" :title="packageInfo?.title">
            <span class="order-value text-ellipsis">{{ packageInfo?.title || '--' }}</span>
          </a-tooltip>
        </div>
        <div class="order-item">
          <span class="order-label">订单编号：</span>
          <a-tooltip placement="topLeft" :title="currentOrderNumber">
            <span class="order-value text-ellipsis">{{ currentOrderNumber || '--' }}</span>
          </a-tooltip>
        </div>
        <div class="order-item">
          <span class="order-label">短信条数：</span>
          <span class="order-value">{{ packageInfo?.count }}</span>
        </div>
        <div class="order-item">
          <span class="order-label">有效期：</span>
          <span class="order-value">{{ getValidDaysText(packageInfo?.validDays) }}</span>
        </div>

        <div class="order-item">
          <span class="order-label">支付金额：</span>
          <div class="price-section">
            <span class="order-price">{{ packageInfo?.totalPrice || '0.00' }}</span>
          </div>
        </div>
      </div>

      <!-- 生成二维码按钮区域 -->
      <div class="generate-qr-section" v-if="!qrCodeGenerated && !paymentSuccess && !paymentError">
        <a-button type="primary" size="large" @click="generateQRCode" :loading="loading" class="generate-qr-btn">
          {{ loading ? '正在生成...' : '生成支付二维码' }}
        </a-button>
        <div class="generate-tip">确认订单信息后点击生成支付二维码</div>
      </div>

      <!-- 二维码显示区域 -->
      <div class="qr-code-section" v-if="qrCodeGenerated && !paymentSuccess && !paymentError">
        <div class="qr-code-header">
          <div class="qr-code-title">请使用微信扫描二维码完成支付</div>
          <a-button type="link" @click="regenerateQRCode" :disabled="loading" class="regenerate-btn">
            <a-icon type="reload" />
            重新生成二维码
          </a-button>
        </div>
        <div class="qr-code-container">
          <div class="qr-code-placeholder">
            <div v-if="loading" class="loading-container">
              <div class="loading-spinner"></div>
              <div class="loading-text">正在生成支付二维码...</div>
            </div>

            <div v-else class="qr-code-image">
              <vue-qr
                :text="downloadData.url"
                :margin="0"
                colorDark="#333"
                colorLight="#fff"
                :logoSrc="downloadData.icon + '?t=' + new Date().getTime()"
                :logoScale="0.2"
                :size="200"
                style="width: 200px; height: 200px"
              ></vue-qr>
            </div>
          </div>
        </div>
        <div class="qr-code-tip">
          二维码有效时间：<span class="time-count">{{ countdown }}</span>
        </div>
      </div>

      <!-- 错误状态 -->
      <div class="error-section" v-if="paymentError">
        <div class="error-container">
          <div class="error-icon">⚠️</div>
          <div class="error-text">生成支付二维码失败</div>
          <a-tooltip placement="topLeft" :title="errorMessage">
            <div class="error-desc text-ellipsis">{{ errorMessage }}</div>
          </a-tooltip>
          <a-button type="primary" @click="retryCreateOrder" class="retry-btn">重新生成</a-button>
        </div>
      </div>

      <!-- 支付成功状态 -->
      <div class="success-section" v-if="paymentSuccess">
        <div class="payment-success">
          <div class="success-icon">✓</div>
          <div class="success-text">支付成功</div>
          <div class="success-desc">系统正在为您开通服务，请稍候...</div>
        </div>
      </div>

      <div class="payment-tips" v-if="qrCodeGenerated && !paymentError">
        <div class="tip-item">
          <span>支付成功后，系统将自动为您开通短信服务</span>
        </div>
        <div class="tip-item">
          <span>如有问题，请联系客服：{{ initPassword || '' }}</span>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
import vueQr from 'vue-qr'
import { addPersonalOrder, checkPayStatus } from '@/api/sms/order'

export default {
  name: 'SmsPackagePaymentModal',
  components: {
    vueQr,
  },
  data() {
    return {
      visible: false,
      paying: false,
      loading: false,
      paymentSuccess: false,
      paymentError: false,
      errorMessage: '',
      countdown: '05:00',
      timer: null,
      pollingTimer: null,
      packageInfo: null,
      currentOrderId: null,
      currentOrderNumber: null,
      downloadData: {
        url: '',
        icon: 'http://hoy-test-1308702491.cos.ap-beijing.myqcloud.com/hoy/oth/IMG_20251107_162000__020544.png',
      },
      initPassword: '',
      qrCodeGenerated: false,
    }
  },
  computed: {
    paymentTitle() {
      const packageName = this.packageInfo?.title || '未知套餐'
      return `支付订单 - ${packageName}`
    },
  },
  methods: {
    async show(packageInfo) {
      this.visible = true
      this.packageInfo = Object.assign({}, packageInfo)
      this.loading = false
      this.paymentSuccess = false
      this.paymentError = false
      this.errorMessage = ''
      this.qrCodeGenerated = false
      this.getConfigKey('CompanyPhone').then((response) => {
        this.initPassword = response.data
      })
    },

    async generateQRCode() {
      this.loading = true
      this.paymentError = false
      this.errorMessage = ''

      try {
        await this.createOrder(this.packageInfo)
        this.qrCodeGenerated = true
      } catch (error) {
      } finally {
        this.loading = false
      }
    },

    async regenerateQRCode() {
      this.loading = true
      this.paymentError = false
      this.errorMessage = ''

      this.clearPolling()
      this.clearCountdown()
      this.currentOrderId = null

      try {
        await this.createOrder(this.packageInfo)
      } catch (error) {
      } finally {
        this.loading = false
      }
    },

    createOrder(packageInfo) {
      return new Promise((resolve, reject) => {
        const form = {
          chooseType: 0,
          packageId: packageInfo.originalData.id,
          payMethod: 0,
          payType: 'WECHAT',
          useCoupon: 0,
          couponCode: null,
          couponId: null,
          remark: '',
        }

        addPersonalOrder(form)
          .then((response) => {
            this.clearPolling()
            this.clearCountdown()
            console.log(response.data, 'response.data')
            this.currentOrderId = response.data.id
            console.log(this.currentOrderId, 'this.currentOrderId')
            this.currentOrderNumber = response.data.payOrderNo || '--'
            this.downloadData.url = `${this.$payUrl}?orderId=${response.data.id}&type=sms`
            console.log(this.downloadData.url, 'this.downloadData.url')
            this.startPollingPaymentStatus()
            this.startCountdown()
            resolve(response)
          })
          .catch((error) => {
            this.handleCreateOrderError(error)
            reject(error)
          })
      })
    },

    handleCreateOrderError(error) {
      this.paymentError = true
      this.qrCodeGenerated = false

      if (error.response && error.response.data) {
        const errorData = error.response.data
        if (errorData.message) {
          this.errorMessage = errorData.message
        } else if (errorData.msg) {
          this.errorMessage = errorData.msg
        } else if (errorData.error) {
          this.errorMessage = errorData.error
        } else {
          this.errorMessage = '创建订单失败，请检查输入参数'
        }
      } else if (error.message && error.message !== 'error') {
        this.errorMessage = error.message
      } else if (error.message === 'error') {
        this.errorMessage = '请求处理失败，请检查网络连接或联系管理员'
      } else {
        this.errorMessage = '网络异常，请稍后重试'
      }
    },

    retryCreateOrder() {
      this.generateQRCode()
    },

    hide() {
      this.visible = false
      this.paying = false
      this.loading = false
      this.paymentSuccess = false
      this.paymentError = false
      this.errorMessage = ''
      this.qrCodeGenerated = false
      this.clearCountdown()
      this.clearPolling()
      this.currentOrderId = null
      this.currentOrderNumber = null
      this.initPassword = ''
    },

    handleCancel() {
      this.hide()
    },

    startPollingPaymentStatus() {
      this.checkPaymentStatus()
      this.pollingTimer = setInterval(() => {
        this.checkPaymentStatus()
      }, 3000)
    },

    async checkPaymentStatus() {
      if (!this.currentOrderId) return

      checkPayStatus({ id: this.currentOrderId })
        .then((response) => {
          if (response.success && response.data === true) {
            this.handlePaymentSuccess()
          }
        })
        .catch((error) => {
          console.error('检查支付状态失败:', error)
        })
    },

    clearPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer)
        this.pollingTimer = null
      }
    },

    handlePaymentSuccess() {
      this.paymentSuccess = true
      this.clearPolling()
      this.clearCountdown()
      this.$message.success('支付成功！短信服务已开通')
      setTimeout(() => {
        this.$emit('payment-success', {
          orderNumber: this.currentOrderNumber,
          amount: this.packageInfo?.totalPrice,
          packageName: this.packageInfo?.title,
          orderId: this.currentOrderId,
        })
        this.hide()
      }, 2000)
    },

    startCountdown() {
      let timeLeft = 300
      this.updateCountdown(timeLeft)
      this.timer = setInterval(() => {
        timeLeft--
        this.updateCountdown(timeLeft)
        if (timeLeft <= 0) {
          this.clearCountdown()
          this.clearPolling()
          this.qrCodeGenerated = false
          this.$message.warning('二维码已过期，请重新生成')
        }
      }, 1000)
    },

    updateCountdown(seconds) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      this.countdown = `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
    },

    clearCountdown() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },

    // 格式化有效期显示
    getValidDaysText(validDays) {
      if (!validDays || validDays === 0) {
        return '永久有效'
      }

      if (validDays >= 365) {
        const years = Math.floor(validDays / 365)
        const remainingDays = validDays % 365
        if (remainingDays === 0) {
          return `${years}年`
        } else {
          return `${years}年${remainingDays}天`
        }
      } else if (validDays >= 30) {
        const months = Math.floor(validDays / 30)
        const remainingDays = validDays % 30
        if (remainingDays === 0) {
          return `${months}个月`
        } else {
          return `${months}个月${remainingDays}天`
        }
      } else {
        return `${validDays}天`
      }
    },
  },

  beforeUnmount() {
    this.clearCountdown()
    this.clearPolling()
  },
}
</script>

<style scoped>
.payment-modal-content {
  padding: 8px;
}

.order-info {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
}

.order-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.order-item:last-child {
  margin-bottom: 0;
}

.order-label {
  color: #666;
  font-size: 14px;
}

.order-value {
  color: #333;
  font-size: 14px;
  font-weight: 500;
}

.price-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.order-price {
  color: #ff4d4f;
  font-size: 18px;
  font-weight: 600;
}

/* 生成二维码按钮区域 */
.generate-qr-section {
  text-align: center;
  margin-bottom: 24px;
  padding: 20px;
  border: 2px dashed #e8e8e8;
  border-radius: 8px;
  background: #fafafa;
}

.generate-qr-btn {
  height: 48px;
  font-size: 16px;
  padding: 0 32px;
}

.generate-tip {
  margin-top: 8px;
  font-size: 14px;
  color: #666;
}

/* 二维码显示区域 */
.qr-code-section {
  text-align: center;
  margin-bottom: 24px;
}

.qr-code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.qr-code-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.regenerate-btn {
  font-size: 14px;
}

.qr-code-container {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.qr-code-placeholder {
  width: 200px;
  height: 200px;
  min-width: 200px;
  min-height: 200px;
  max-width: 200px;
  max-height: 200px;
  background: #f5f5f5;
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-code-image {
  text-align: center;
  width: 200px;
  height: 200px;
  min-width: 200px;
  min-height: 200px;
  max-width: 200px;
  max-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-code-image ::v-deep canvas {
  width: 200px !important;
  height: 200px !important;
  min-width: 200px !important;
  min-height: 200px !important;
  max-width: 200px !important;
  max-height: 200px !important;
}

.qr-code-tip {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.time-count {
  color: #ff4d4f;
  font-weight: 500;
}

/* 错误状态区域 */
.error-section {
  text-align: center;
  margin-bottom: 24px;
  padding: 20px;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.error-text {
  font-size: 16px;
  color: #ff4d4f;
  font-weight: 500;
  margin-bottom: 8px;
}

.error-desc {
  font-size: 14px;
  color: #666;
  text-align: center;
  margin-bottom: 16px;
  max-width: 300px;
}

.retry-btn {
  height: 36px;
  font-size: 14px;
}

/* 成功状态区域 */
.success-section {
  text-align: center;
  margin-bottom: 24px;
  padding: 20px;
}

.payment-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.success-icon {
  width: 60px;
  height: 60px;
  background: #52c41a;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  font-weight: bold;
  margin-bottom: 16px;
}

.success-text {
  font-size: 18px;
  color: #52c41a;
  font-weight: 500;
  margin-bottom: 8px;
}

.success-desc {
  font-size: 14px;
  color: #666;
}

.payment-tips {
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 6px;
  padding: 12px;
}

.tip-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

.tip-item:last-child {
  margin-bottom: 0;
}

.tip-item span:last-child {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4f46e5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

.loading-text {
  font-size: 14px;
  color: #666;
}

/* 文字省略号样式 */
.text-ellipsis {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
